'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createLeagueChannelMapping,
  createOttMapping,
  createYouTubeChannel,
  deleteLeagueChannelMapping,
  deleteOttMapping,
  deleteYouTubeChannel,
  listLeagueChannelMappings,
  listMappingHistory,
  listOttMappingOptions,
  listOttMappings,
  listYouTubeChannels,
  updateLeagueChannelMapping,
  updateOttMapping,
  updateYouTubeChannel,
  type CreateLeagueChannelMappingInput,
  type CreateOttMappingInput,
  type CreateYouTubeChannelInput,
  type UpdateLeagueChannelMappingInput,
  type UpdateOttMappingInput,
  type UpdateYouTubeChannelInput,
} from '@nomiss/services';

export const adminPlatformsQueryKeys = {
  all: ['admin', 'platforms'] as const,
  ottMappings: () =>
    [...adminPlatformsQueryKeys.all, 'ott-mappings'] as const,
  ottMappingOptions: () =>
    [...adminPlatformsQueryKeys.all, 'ott-mapping-options'] as const,
  youtubeChannels: () =>
    [...adminPlatformsQueryKeys.all, 'youtube-channels'] as const,
  leagueMappings: () =>
    [...adminPlatformsQueryKeys.all, 'league-mappings'] as const,
  mappingHistory: (entityId?: string) =>
    [...adminPlatformsQueryKeys.all, 'mapping-history', entityId ?? ''] as const,
};

export function useAdminOttMappings(options?: { enabled?: boolean }) {
  const adminOttMappingsQuery = useQuery({
    queryKey: adminPlatformsQueryKeys.ottMappings(),
    queryFn: () => listOttMappings(),
    enabled: options?.enabled ?? true,
  });

  return { adminOttMappingsQuery };
}

export function useAdminOttMappingOptions(options?: { enabled?: boolean }) {
  const adminOttMappingOptionsQuery = useQuery({
    queryKey: adminPlatformsQueryKeys.ottMappingOptions(),
    queryFn: () => listOttMappingOptions(),
    enabled: options?.enabled ?? true,
  });

  return { adminOttMappingOptionsQuery };
}

export function useCreateOttMapping() {
  const queryClient = useQueryClient();

  const createOttMappingMutation = useMutation({
    mutationFn: (input: CreateOttMappingInput) => createOttMapping(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adminPlatformsQueryKeys.ottMappings(),
      });
    },
  });

  return { createOttMappingMutation };
}

export function useUpdateOttMapping() {
  const queryClient = useQueryClient();

  const updateOttMappingMutation = useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: UpdateOttMappingInput;
    }) => updateOttMapping(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adminPlatformsQueryKeys.ottMappings(),
      });
    },
  });

  return { updateOttMappingMutation };
}

export function useDeleteOttMapping() {
  const queryClient = useQueryClient();

  const deleteOttMappingMutation = useMutation({
    mutationFn: (id: string) => deleteOttMapping(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adminPlatformsQueryKeys.ottMappings(),
      });
    },
  });

  return { deleteOttMappingMutation };
}

export function useAdminYouTubeChannels(options?: { enabled?: boolean }) {
  const adminYouTubeChannelsQuery = useQuery({
    queryKey: adminPlatformsQueryKeys.youtubeChannels(),
    queryFn: () => listYouTubeChannels(),
    enabled: options?.enabled ?? true,
  });

  return { adminYouTubeChannelsQuery };
}

export function useCreateYouTubeChannel() {
  const queryClient = useQueryClient();

  const createYouTubeChannelMutation = useMutation({
    mutationFn: (input: CreateYouTubeChannelInput) =>
      createYouTubeChannel(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adminPlatformsQueryKeys.youtubeChannels(),
      });
    },
  });

  return { createYouTubeChannelMutation };
}

export function useUpdateYouTubeChannel() {
  const queryClient = useQueryClient();

  const updateYouTubeChannelMutation = useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: UpdateYouTubeChannelInput;
    }) => updateYouTubeChannel(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adminPlatformsQueryKeys.youtubeChannels(),
      });
      void queryClient.invalidateQueries({
        queryKey: adminPlatformsQueryKeys.leagueMappings(),
      });
    },
  });

  return { updateYouTubeChannelMutation };
}

export function useDeleteYouTubeChannel() {
  const queryClient = useQueryClient();

  const deleteYouTubeChannelMutation = useMutation({
    mutationFn: (id: string) => deleteYouTubeChannel(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adminPlatformsQueryKeys.youtubeChannels(),
      });
      void queryClient.invalidateQueries({
        queryKey: adminPlatformsQueryKeys.leagueMappings(),
      });
    },
  });

  return { deleteYouTubeChannelMutation };
}

export function useAdminLeagueChannelMappings(options?: {
  enabled?: boolean;
}) {
  const adminLeagueChannelMappingsQuery = useQuery({
    queryKey: adminPlatformsQueryKeys.leagueMappings(),
    queryFn: () => listLeagueChannelMappings(),
    enabled: options?.enabled ?? true,
  });

  return { adminLeagueChannelMappingsQuery };
}

export function useCreateLeagueChannelMapping() {
  const queryClient = useQueryClient();

  const createLeagueChannelMappingMutation = useMutation({
    mutationFn: (input: CreateLeagueChannelMappingInput) =>
      createLeagueChannelMapping(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adminPlatformsQueryKeys.leagueMappings(),
      });
    },
  });

  return { createLeagueChannelMappingMutation };
}

export function useUpdateLeagueChannelMapping() {
  const queryClient = useQueryClient();

  const updateLeagueChannelMappingMutation = useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: UpdateLeagueChannelMappingInput;
    }) => updateLeagueChannelMapping(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adminPlatformsQueryKeys.leagueMappings(),
      });
    },
  });

  return { updateLeagueChannelMappingMutation };
}

export function useDeleteLeagueChannelMapping() {
  const queryClient = useQueryClient();

  const deleteLeagueChannelMappingMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      deleteLeagueChannelMapping(id, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adminPlatformsQueryKeys.leagueMappings(),
      });
    },
  });

  return { deleteLeagueChannelMappingMutation };
}

export function useAdminMappingHistory(entityId?: string) {
  const adminMappingHistoryQuery = useQuery({
    queryKey: adminPlatformsQueryKeys.mappingHistory(entityId),
    queryFn: () => listMappingHistory(entityId),
    enabled: !!entityId,
  });

  return { adminMappingHistoryQuery };
}
